import * as moment from 'moment';

/**
 * Omit a key from an object
 */
export const omitKey = (object: { [k: string]: any }, key: string | number): { [k: string]: any } =>
  ({ [key]: key, ...object } = object) && object;

/**
 * Convert ISO formatted number string to normal number
 */
export const convertISONumberToNormal = (isoNumber: string | number): number =>
  typeof isoNumber === 'string' ? +isoNumber.replace(/,/g, '') : isoNumber;

/**
 * Convert ISO formatted number string to normal, optionally keeping signs and zeros
 */
export const convertISONumberToNormalKeepSigns = (
  isoNumber: string | number,
  keepSignAndZero = false
): string | number => {
  if (typeof isoNumber === 'string') {
    const cleanedNumber = isoNumber.replace(/,/g, '');

    if (keepSignAndZero) {
      return cleanedNumber;
    }

    return cleanedNumber.replace(/^(\+|0)/, '');
  }

  return isoNumber;
};

/**
 * Get file extension from File object or filename string
 */
export function getFileExtension(fileOrName: File | string): string {
  const fileName = typeof fileOrName === 'string' ? fileOrName : fileOrName.name;
  return fileName.substring(fileName.lastIndexOf('.') + 1);
}

/**
 * Restrict decimal value length in input field
 */
export const restrictDecimalValueLength = (inputField: any, length = 4): string => {
  const match = inputField.value.match(/^(\d+(\.\d{0,4})?)?/);
  return match ? match[0] : '';
};

/**
 * Extract error list from error data object
 */
export const extractErrorList = (errorData: Record<string, unknown>): string[] => {
  let messages: string[] = [];
  for (const field in errorData) {
    if (typeof errorData[field] === 'string') {
      messages.push(errorData[field] as string);
    } else if (typeof errorData[field] === 'object') {
      if (Array.isArray(errorData[field])) {
        for (const item of errorData[field] as unknown[]) {
          if (typeof item === 'string') {
            messages = [...messages, item];
          } else {
            const innerMessage = extractErrorList(item as Record<string, unknown>);
            messages = [...messages, ...innerMessage];
          }
        }
      } else {
        const innerMessage = extractErrorList(errorData[field] as Record<string, unknown>);
        messages = [...messages, ...innerMessage];
      }
    }
  }
  return messages;
};

/**
 * General object cleaner (removes falsy values)
 */
export function cleanObject(query: any): any {
  const object: any = {};
  for (const key in query) {
    if (query[key]) {
      object[key] = query[key];
    }
  }
  return object;
}

/**
 * Parse formatted number strings to numbers, handling multiple international formats
 * Supports European format (1.234,56), US format (1,234.56), and simple decimal formats
 */
export function parseNumberString(text: string): number | null {
  if (text == null) return null;
  text = text.substring(0, 17);
  let str = String(text).trim();

  // keep only one leading '-' and remove other '-' occurrences
  str = str.replace(/(?!^)-/g, "");
  str = str.replace(/^-+/, "-");

  // remove spaces used as thousands separators
  str = str.replace(/\s+/g, "");

  const hasDot = str.indexOf('.') !== -1;
  const hasComma = str.indexOf(',') !== -1;

  if (hasDot && hasComma) {
    // both present -> the last separator is the decimal separator
    if (str.lastIndexOf('.') > str.lastIndexOf(',')) {
      // dot is decimal -> remove commas (thousand separators)
      str = str.replace(/,/g, '');
    } else {
      // comma is decimal -> remove dots and replace comma with dot
      str = str.replace(/\./g, '').replace(',', '.');
    }
  } else if (hasComma && !hasDot) {
    const commaCount = (str.match(/,/g) || []).length;
    if (commaCount > 1) {
      // multiple commas -> treat as thousands separators
      str = str.replace(/,/g, '');
    } else {
      // single comma -> decimal separator
      str = str.replace(',', '.');
    }
  } else if (hasDot && !hasComma) {
    const dotCount = (str.match(/\./g) || []).length;
    if (dotCount > 1) {
      // multiple dots -> treat as thousands separators
      str = str.replace(/\./g, '');
    } else {
      // single dot -> decimal separator (leave as-is)
      // nothing to do
    }
  }
  // else: no dot or comma -> plain integer string

  const num = parseFloat(str);
  return Number.isFinite(num) ? num : null;
}

/**
 * Filter params with options for AG Grid
 */
export const filterParamsWithOptions = {
  maxNumConditions: 1,
  debounceMs: 500,
  allowedCharPattern: '\\d\\.,%-',
  numberParser: parseNumberString,
  filterOptions: [
    {
      displayKey: 'all',
      displayName: 'All',
      predicate: () => true,
    },
    {
      displayKey: 'Blank',
      displayName: 'Blank',
      predicate: () => true,
      numberOfInputs: 0,
    },
    {
      displayKey: 'Filled',
      displayName: 'Filled',
      predicate: () => true,
      numberOfInputs: 0,
    },
    {
      displayKey: 'Equals',
      displayName: 'Equals',
      predicate: () => true,
    },
    {
      displayKey: 'StartsWith',
      displayName: 'Starts With',
      predicate: () => true,
    },
    {
      displayKey: 'NotEquals',
      displayName: 'Not Equals',
      predicate: () => true,
    },
    {
      displayKey: 'GreaterThan',
      displayName: 'Greater Than',
      predicate: () => true,
    },
    {
      displayKey: 'LessThan',
      displayName: 'Less Than',
      predicate: () => true,
    },
  ],
};

/**
 * Check if type matches value, or returns true if type is undefined/null
 * Used in pinned row calculations to determine if a type filter is active
 */
export function matchesType(type: any, value: string): boolean {
  return type === value || !type;
}

/**
 * Check if a number is in scientific notation
 */
export function isScientificNotation(number: number): boolean {
  // Convert the number to a string
  const numberString = number.toString();

  // Define a regular expression pattern to match scientific notation
  const scientificNotationPattern = /^[+-]?\d+(\.\d+)?[eE][+-]?\d+$/;

  // Test if the number matches the pattern
  return scientificNotationPattern.test(numberString);
}

/**
 * Batch comparator for AG Grid
 */
export function batchComparator(params: any): number {
  if (!params.valueA || !params.valueB) {
    if (!params.valueA && !params.valueB) return 0;
    return !params.valueA ? -1 : 1;
  }
  const [prefixA, numberPartA] = params.valueA.split('-');
  const [prefixB, numberPartB] = params.valueB.split('-');
  if (prefixA !== prefixB) {
    return prefixA.localeCompare(prefixB);
  }
  const numA = parseInt(numberPartA, 10);
  const numB = parseInt(numberPartB, 10);
  if (isNaN(numA) && isNaN(numB)) return 0;
  if (isNaN(numA)) return 1;
  if (isNaN(numB)) return -1;
  if (numA < 10 && numB >= 10) return -1;
  if (numB < 10 && numA >= 10) return 1;
  return numA - numB;
}

/**
 * String value comparator for AG Grid
 */
export function stringValueComparator(valueA: string, valueB: string) {
  const parseValue = (value: string) => value?.match(/([A-Za-z]*)(\d*)/) || [];
  const [_, alphaA = '', numA = ''] = parseValue(valueA || '');
  const [__, alphaB = '', numB = ''] = parseValue(valueB || '');
  if (alphaA !== alphaB) {
    return alphaA.localeCompare(alphaB);
  }
  return Number(numA) - Number(numB);
}
